import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { Select, Tooltip } from "antd";
import { RiErrorWarningLine } from "react-icons/ri";
import { useTranslation } from "react-i18next";

import { CopyButton, DataTable, RowActionsMenu, TruncatedText } from "../../../components/data-table";
import {
    RESERVATION_FILTER_KEYS as KEYS,
    RESERVATION_LIST_ACTION_KEYS as ACTIONS,
    RESERVATION_TAB_KEYS as TABS,
    buildReservationActionItems,
    endReservation,
    formatReservationDate,
    formatReservationPhone,
    formatReservationTime,
    getAssignmentOptions,
    getCarLocation,
    getReservationFilterChipProps,
    getReservationStatusBadge,
    getTabSectionTitleKey,
    startReservation,
    viewReservation,
} from "../list";

function DateTimeCell({ value }) {
    if (!value) return <span className="text-gray-400">—</span>;
    return (
        <div className="flex flex-col leading-tight">
            <span className="text-sm text-gray-900">{formatReservationDate(new Date(value))}</span>
            <span className="text-xs text-gray-500">{formatReservationTime(new Date(value))}</span>
        </div>
    );
}

DateTimeCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

function ReservationTable({ list }) {
    const { t } = useTranslation("reservations");
    const history = useHistory();
    const { tabKey, table, assignment, filteredRows, pageRows, loading } = list;

    const handleAction = (key, reservation) => {
        if (key === ACTIONS.VIEW) viewReservation(history, reservation);
        if (key === ACTIONS.START_RESERVATION) startReservation(history, reservation);
        if (key === ACTIONS.END_RESERVATION) endReservation(history, reservation);
    };

    const renderIdWarning = (reservation) => {
        if (tabKey === TABS.UPCOMING && !reservation?.carSelected) {
            return (
                <Tooltip title={t("_upcoming.carNeededWarning")}>
                    <RiErrorWarningLine size={16} className="shrink-0 text-[#ff8800]" />
                </Tooltip>
            );
        }
        if (tabKey === TABS.ONGOING && new Date(reservation.dropoffDate) < new Date()) {
            return (
                <Tooltip title={t("_ongoing.overdueReservation")}>
                    <RiErrorWarningLine size={16} className="shrink-0 text-[#ff8800]" />
                </Tooltip>
            );
        }
        return null;
    };

    const columns = [
        {
            title: t("reservationID"),
            dataIndex: "id",
            width: 160,
            ...table.getColumnSearchProps(KEYS.ID),
            render: (id, reservation) => (
                <div className="flex items-center gap-1.5">
                    <Link to={`/reservations/${id}`} className="sc-table-link sc-mono">
                        {id?.slice(0, 10)}
                    </Link>
                    <CopyButton value={id} />
                    {renderIdWarning(reservation)}
                </div>
            ),
        },
        {
            title: t("customer"),
            dataIndex: "customerName",
            width: 220,
            ...table.getColumnSearchProps(KEYS.CUSTOMER),
            render: (customerName, reservation) => (
                <div className="flex min-w-0 flex-col leading-tight">
                    <TruncatedText>
                        <Link
                            to={`/customers/${reservation?.userUID}`}
                            className="sc-table-link"
                            aria-label={t("_all.viewCustomer")}>
                            {customerName || t("notAvailable")}
                        </Link>
                    </TruncatedText>
                    <TruncatedText className="text-xs text-gray-500">
                        {formatReservationPhone(reservation.mobileNumber) || reservation.email}
                    </TruncatedText>
                </div>
            ),
        },
        {
            title: t("pickupTime"),
            dataIndex: "pickupDate",
            width: 130,
            render: (value) => <DateTimeCell value={value} />,
        },
        {
            title: t("dropoffTime"),
            dataIndex: "dropoffDate",
            width: 130,
            render: (value) => <DateTimeCell value={value} />,
        },
        {
            title: t("iadvreq.carType"),
            dataIndex: "carType",
            width: 180,
            ...table.getColumnSearchProps(KEYS.CAR_TYPE),
            render: (carType, reservation) => (
                <div className="flex min-w-0 flex-col leading-tight">
                    <TruncatedText className="text-sm">{carType || t("notAvailable")}</TruncatedText>
                    {reservation?.carSelected?.carName && (
                        <TruncatedText className="text-xs text-gray-500">
                            {reservation.carSelected.carName}
                        </TruncatedText>
                    )}
                </div>
            ),
        },
        {
            title: t("vin"),
            key: "vin",
            width: 220,
            ...table.getColumnSearchProps(KEYS.VIN),
            render: (_, reservation) => {
                const vin = reservation?.carSelected?.VIN;
                if (!vin) {
                    return <span className="text-sm text-gray-400">{t("carNotAssigned")}</span>;
                }
                return (
                    <div className="flex items-center gap-1.5">
                        <Tooltip title={reservation.carSelected.carNumber && `${t("plateNumber")}: ${reservation.carSelected.carNumber}`}>
                            <Link
                                to={`/cardetails/${vin}`}
                                className="sc-table-link sc-mono"
                                aria-label={t("_all.viewCar")}>
                                {vin}
                            </Link>
                        </Tooltip>
                        <CopyButton value={vin} />
                    </div>
                );
            },
        },
        {
            title: t("location"),
            key: "location",
            width: 180,
            ellipsis: true,
            ...table.getColumnSearchProps(KEYS.LOCATION),
            render: (_, reservation) => getCarLocation(reservation) || t("notAvailable"),
        },
    ];

    if (tabKey === TABS.ALL) {
        columns.push({
            title: t("status"),
            key: "status",
            width: 120,
            render: (_, reservation) => {
                const badge = getReservationStatusBadge(reservation, tabKey, t);
                return (
                    <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${badge.className}`}>
                        {badge.label}
                    </span>
                );
            },
        });
    }

    columns.push({
        title: <span className="sr-only">{t("action")}</span>,
        key: "action",
        width: 64,
        align: "center",
        fixed: "right",
        render: (_, reservation) => (
            <RowActionsMenu
                label={t("action")}
                items={buildReservationActionItems(reservation, tabKey, t)}
                onClick={({ key }) => handleAction(key, reservation)}
            />
        ),
    });

    const assignmentOptions = getAssignmentOptions(t);

    return (
        <DataTable
                title={t(getTabSectionTitleKey(tabKey))}
                count={filteredRows.length}
                filters={table}
                search={{ key: KEYS.SEARCH, placeholder: t("listSearchPlaceholder") }}
                {...getReservationFilterChipProps(t)}
                toolbarExtra={
                    tabKey === TABS.UPCOMING && (
                        <Select
                            value={assignment}
                            onChange={(value) => table.setFilter(KEYS.ASSIGNMENT, value === "all" ? null : value)}
                            options={assignmentOptions}
                            popupMatchSelectWidth={false}
                            aria-label={t("carAssignment")}
                            className="min-w-48"
                        />
                    )
                }
                columns={columns}
                dataSource={pageRows}
                loading={loading}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={{
                    total: filteredRows.length,
                    current: list.currentPage,
                    pageSize: list.pageSize,
                    onChange: list.setCurrentPage,
                    onPageSizeChange: list.setPageSize,
                }}
            />
    );
}

ReservationTable.propTypes = {
    list: PropTypes.shape({
        tabKey: PropTypes.oneOf(Object.values(TABS)).isRequired,
        table: PropTypes.object.isRequired,
        assignment: PropTypes.string.isRequired,
        filteredRows: PropTypes.array.isRequired,
        pageRows: PropTypes.array.isRequired,
        loading: PropTypes.bool.isRequired,
        currentPage: PropTypes.number.isRequired,
        setCurrentPage: PropTypes.func.isRequired,
        pageSize: PropTypes.number.isRequired,
        setPageSize: PropTypes.func.isRequired,
    }).isRequired,
};

export default ReservationTable;
